// No useState needed since we're using store state
import { useEffect, useRef, useState } from "react";
import { useLiveSessionStore } from "../../../stores/useLiveSessionStore";
import "./ScreenSlides.scss";

// Environment-specific CDN base URL
const RESOURCES_CDN_BASE_URL = import.meta.env.VITE_RESOURCES_CDN_BASE_URL;

interface ScreenSlidesProps {
  height?: number;
  activeSlideId?: number; // Added to track which slide is active
  slideImages?: string[]; // Added to allow custom slide images
  onSlideChange?: (slideId: number) => void; // Added to notify parent of slide changes
  materialId?: string; // Material ID for constructing thumbnail URLs
  screenData?: Array<{
    id: string;
    slide_number: string;
    slide_path: string;
    slide_thumbnail_extension: string;
    type: string;
  }>;
}

export const ScreenSlides = (props: ScreenSlidesProps) => {
  // Get screen data and engine functions from Zustand store
  const isEngineLoaded = useLiveSessionStore((state) => state.isEngineLoaded);
  const screenData = useLiveSessionStore((state) => state.screenData);
  const materialId = useLiveSessionStore(
    (state) => state.sessionData?.materialId
  );
  const sendMessageToEngine = useLiveSessionStore(
    (state) => state.sendMessageToEngine
  );
  const setActiveSlideIndex = useLiveSessionStore(
    (state) => state.setActiveSlideIndex
  );

  // Get active slide index from store instead of local state
  const activeSlideIndex = useLiveSessionStore(
    (state) => state.activeSlideIndex
  );

  // Use activeSlideIndex from store, or props.activeSlideId as fallback
  const activeSlide = props.activeSlideId ?? activeSlideIndex;

  const [furthestSlideIndex, setFurthestSlideIndex] = useState<number | null>(
    () => {
      const savedIndex = localStorage.getItem("furthestSlideIndex");
      if (savedIndex !== null && !isNaN(Number(savedIndex))) {
        return Number(savedIndex);
      }
      return null;
    }
  );

  // Persist active slide index to localStorage whenever it changes
  useEffect(() => {
    if (activeSlideIndex !== undefined && activeSlideIndex !== null) {
      localStorage.setItem("activeSlideIndex", String(activeSlideIndex));
    }
  }, [activeSlideIndex]);

  // Track the furthest slide the user has reached to keep it enabled
  useEffect(() => {
    if (activeSlideIndex === undefined || activeSlideIndex === null) return;

    setFurthestSlideIndex((prev) => {
      const next =
        prev === null ? activeSlideIndex : Math.max(prev, activeSlideIndex);
      return next === prev ? prev : next;
    });
  }, [activeSlideIndex]);

  useEffect(() => {
    if (furthestSlideIndex !== null) {
      localStorage.setItem("furthestSlideIndex", String(furthestSlideIndex));
    }
  }, [furthestSlideIndex]);

  // On mount, restore active slide index from localStorage if available
  useEffect(() => {
    const savedIndex = localStorage.getItem("activeSlideIndex");
    if (savedIndex !== null && !isNaN(Number(savedIndex))) {
      setActiveSlideIndex(Number(savedIndex));
    }
  }, [setActiveSlideIndex]);

  const hasSlidesData = Boolean(materialId && screenData.length > 0);

  // Construct thumbnail URLs from screen data in store when available
  let slideImages: string[] = [];
  if (props.slideImages) {
    slideImages = props.slideImages;
  } else if (hasSlidesData) {
    slideImages = screenData.map((screen) => {
      const url = `${RESOURCES_CDN_BASE_URL}/sessions-packages/interactive/${materialId}/${materialId}/${screen.slide_path}/${screen.id}_thumbnail.${screen.slide_thumbnail_extension}`;
      console.log("Generated slide URL:", url);
      return url;
    });
  }

  const slidesCount = props.slideImages
    ? props.slideImages.length
    : hasSlidesData
    ? screenData.length
    : 0;

  const normalizedActiveSlide =
    activeSlide !== undefined && activeSlide !== null
      ? Math.min(Math.max(activeSlide, 0), Math.max(slidesCount - 1, 0))
      : 0;

  const highestUnlockedSlide =
    furthestSlideIndex !== null
      ? Math.max(furthestSlideIndex, normalizedActiveSlide)
      : normalizedActiveSlide;

  const slideRefs = useRef<Array<HTMLDivElement | null>>([]);
  const slidesContainerRef = useRef<HTMLDivElement | null>(null);

  // State to track if we can scroll left or right
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollButtonsVisibility = () => {
    if (!slidesContainerRef.current) return;

    const container = slidesContainerRef.current;
    const scrollLeft = container.scrollLeft;
    const scrollWidth = container.scrollWidth;
    const clientWidth = container.clientWidth;

    setCanScrollLeft(scrollLeft > 5);

    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 5);
  };

  const scrollActiveSlideIntoView = () => {
    const target = slideRefs.current[normalizedActiveSlide];
    if (target) {
      target.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
    }
  };

  // Scroll the slides container left or right
  const handleScrollSlides = (direction: "left" | "right") => {
    if (!slidesContainerRef.current) return;

    const container = slidesContainerRef.current;
    const scrollAmount = container.clientWidth * 0.8;

    container.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    scrollActiveSlideIntoView();
  }, [normalizedActiveSlide, slidesCount]);

  useEffect(() => {
    const handleResize = () => {
      scrollActiveSlideIntoView();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const container = slidesContainerRef.current;
    if (!container) return;

    updateScrollButtonsVisibility();
    container.addEventListener("scroll", updateScrollButtonsVisibility);

    return () => {
      container.removeEventListener("scroll", updateScrollButtonsVisibility);
    };
  }, [slidesCount]);

  useEffect(() => {
    updateScrollButtonsVisibility();
  }, [slidesCount]);

  if (!isEngineLoaded) {
    return (
      <div
        className="screen-slides-loading"
        style={{ height: props.height ? `${props.height}px` : undefined }}
      >
        <div className="slides-loading">
          <div className="loading-spinner-small"></div>
          <p>Initializing engine...</p>
        </div>
      </div>
    );
  }

  // Don't render slides until we have screen data
  if (!hasSlidesData) {
    return (
      <div
        className="screen-slides-container"
        style={{ height: props.height ? `${props.height}px` : undefined }}
      >
        <div className="slides-loading">
          <p>Loading slides...</p>
        </div>
      </div>
    );
  }

  // Allow navigating to any previous slide (already visited),
  // the current slide, or the immediate next slide (to advance).
  const isSlideEnabled = (slideIndex: number) => {
    if (slidesCount === 0) return false;
    if (slideIndex <= highestUnlockedSlide) return true;
    if (slideIndex === highestUnlockedSlide + 1 && slideIndex < slidesCount)
      return true;
    return false;
  };

  // Handle slide click to toggle active state and navigate in engine
  const handleSlideClick = (slideId: number) => {
    if (!isSlideEnabled(slideId)) return;

    setActiveSlideIndex(slideId); // Update store with active slide index

    // Navigate to slide using the engine's seekToSlide method
    if (screenData.length > 0 && slideId < screenData.length) {
      const targetScreen = screenData[slideId];
      try {
        sendMessageToEngine("updateScreen", { screenID: targetScreen.id });
      } catch (error) {
        console.error("Error navigating to slide:", error);
      }
    }

    // Notify parent component if callback provided
    if (props.onSlideChange) {
      props.onSlideChange(slideId);
    }
  };

  return (
    <div className="screen-slides-container" style={{ height: props.height }}>
      <div className="slides-container">
        <div className="slides" ref={slidesContainerRef}>
          {[...Array(slidesCount)].map((_, idx) => {
            const enabled = isSlideEnabled(idx);
            return (
              <div
                className={`slide ${activeSlide === idx ? "active" : ""} ${
                  enabled ? "" : "disabled"
                }`}
                key={idx}
                ref={(el) => {
                  slideRefs.current[idx] = el;
                }}
                onClick={() => handleSlideClick(idx)}
                aria-disabled={!enabled}
                style={{
                  cursor: enabled ? "pointer" : "not-allowed",
                }}
              >
                <img
                  src={slideImages[idx % slideImages.length]}
                  alt={`Slide ${idx + 1}`}
                />
                <div className="slide__num">{idx + 1}</div>
              </div>
            );
          })}
        </div>
        <div className="slides-navigation">
          <button
            className={`slides-navigation__button left-btn btn ${
              !canScrollLeft ? "hidden" : ""
            }`}
            onClick={() => handleScrollSlides("left")}
            disabled={!canScrollLeft}
          >
            <i className="f-icon chevron-left-icon"></i>
          </button>
          <button
            className={`slides-navigation__button right-btn btn ${
              !canScrollRight ? "hidden" : ""
            }`}
            onClick={() => handleScrollSlides("right")}
            disabled={!canScrollRight}
          >
            <i className="f-icon chevron-right-icon"></i>
          </button>
        </div>
      </div>
    </div>
  );
};
